import React from "react"
import { FormProvider } from "react-hook-form"
import { useTranslation } from "react-i18next"

import {
  ProjectPDFGeneralDataOption,
  ProjectPDFInstallationReferencesOption,
  ProjectPDFPriceOption,
  ProjectPDFRoomDataOption
} from "shared/types/PDFExport"
import { FormProviderProps } from "shared/types/ReactHookForm"
import { SelectOption } from "shared/types/Select"
import { CheckboxList } from "shared/ui/input/select/checkbox/CheckboxList"
import { Select } from "shared/ui/input/select/select/Select"
import { FormOverlay } from "shared/ui/overlay/FormOverlay"

import { ProjectPDFOverlayFormData } from "./ProjectPDFOverlay"
import "./styles/_project-pdf-overlay.scss"

export interface ProjectPDFOverlayViewModel {
  form: FormProviderProps<ProjectPDFOverlayFormData>
  positionDataOptions: SelectOption<ProjectPDFGeneralDataOption>[]
  roomDataOptions: SelectOption<ProjectPDFRoomDataOption>[]
  priceDataOptions: SelectOption<ProjectPDFPriceOption>[]
  installationReferenceOptions: SelectOption<ProjectPDFInstallationReferencesOption>[]
  autoFocus?: boolean
  onSubmit: () => void
  onCancel: () => void
}

export const ProjectPDFOverlayView = React.memo(
  ({
    autoFocus = true,
    form,
    positionDataOptions,
    roomDataOptions,
    priceDataOptions,
    installationReferenceOptions,
    onSubmit,
    onCancel
  }: ProjectPDFOverlayViewModel) => {
    const { t } = useTranslation()

    return (
      <FormOverlay
        id="project-pdf-export-overlay"
        form="project-pdf-export-form"
        className="project-pdf-export-overlay"
        headline={t("Project.Actions.ExportPDF")}
        onCancel={onCancel}
        submitButtonText={t("General.Export")}
        showCloseButton
      >
        <FormProvider {...form}>
          <form
            id="project-pdf-export-form"
            className="overlay-form"
            onSubmit={onSubmit}
          >
            <div className="project-export-overlay__intro-text">
              {t("ProjectExport.IntroText")}
            </div>

            <div className="project-export-overlay__checkbox-options">
              <CheckboxList.Root name="room" label={t("ProjectExport.Rooms")}>
                <CheckboxList.Label className="project-export-overlay__options-label" />
                <CheckboxList.Input
                  options={roomDataOptions}
                  autoFocus={autoFocus}
                />
              </CheckboxList.Root>

              <CheckboxList.Root
                name="position"
                label={t("ProjectExport.Positions")}
              >
                <CheckboxList.Label className="project-export-overlay__options-label" />
                <CheckboxList.Input
                  options={positionDataOptions}
                  autoFocus={autoFocus}
                />
              </CheckboxList.Root>
            </div>

            <Select.Root
              name="price"
              inputID="project-pdf-price-options"
              label={t("ProjectExport.Prices")}
            >
              <Select.Label className="project-export-overlay__options-label" />
              <Select.Input options={priceDataOptions} />
            </Select.Root>

            <Select.Root
              name="installationReference"
              inputID="project-pdf-installation-reference-options"
              label={t("InstallationReference.InstallationReferences")}
            >
              <Select.Label className="project-export-overlay__options-label" />
              <Select.Input options={installationReferenceOptions} />
            </Select.Root>
          </form>
        </FormProvider>
      </FormOverlay>
    )
  }
)
